import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Sparkles,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Search,
  Receipt,
  ShoppingCart,
  ChevronLeft,
  Target,
  Lightbulb,
  Boxes,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import CountUp from "../components/CountUp";
import PulseScoreBadge from "../components/PulseScoreBadge";
import ActionPlanModal from "../components/ActionPlanModal";
import ExplainButton from "../components/ExplainButton";
import QuickJumpBar from "../components/QuickJumpBar";
import ExecutiveBrief from "../components/ExecutiveBrief";
import CherryPicker from "../components/CherryPicker";
import ExecutiveActionsInline from "../components/ExecutiveActionsInline";
import ExecutiveActionsModal from "../components/ExecutiveActionsModal";
import LivePulseIndicator from "../components/LivePulseIndicator";
import DeadCapitalStrip from "../components/DeadCapitalStrip";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  track,
} from "../lib/api";
import { useHomeBundle } from "../lib/queries";

const fmtHebDate = () =>
  new Date().toLocaleDateString("he-IL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const greet = (name) => {
  const h = new Date().getHours();
  const part =
    h < 5 ? "לילה טוב" : h < 12 ? "בוקר טוב" : h < 17 ? "צהריים טובים" : h < 21 ? "ערב טוב" : "לילה טוב";
  return `${part}, ${name || "אסף"}`;
};

export default function HomeFeed() {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("pulse_user") || "{}"), []);
  const [granularity, setGranularity] = useState("day");
  const [planOpen, setPlanOpen] = useState(false);
  // React Query bundle: 11 endpoints cached together. After first visit,
  // returning home from any other page is instant (was ~1.5s every time).
  const { data: bundle, isLoading: bundleLoading } = useHomeBundle();
  const loading = bundleLoading;
  const summary = bundle?.summary || null;
  const insights = bundle?.insights || [];
  const headline = bundle?.headline || null;
  const monthly = bundle?.monthly || [];
  const timeseries = bundle?.timeseries || null;
  const marked = bundle?.marked || [];
  const recommendations = bundle?.recommendations || [];
  const invKpis = bundle?.invKpis || null;
  const zeroPrice = bundle?.zeroPrice || null;
  const onTheWay = bundle?.onTheWay || null;
  const families = bundle?.families || null;
  const [execActionsOpen, setExecActionsOpen] = useState(false);

  // Aggregate count for the "First Scan" headline
  const firstScanCount =
    (invKpis?.critical_restock_count || 0) +
    (invKpis?.stockouts_count || 0) +
    (zeroPrice?.total_count || 0) +
    ((onTheWay?.supported && onTheWay?.total_rows) || 0) +
    (families?.supported ? families.total_count : 0);

  return (
    <div className="px-4 sm:px-5 lg:px-8 py-5 lg:py-6 w-full overflow-x-hidden" data-testid="home-feed">
      {/* ===== Hero Greeting Row ===== */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-5 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3"
      >
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-1.5 flex items-center gap-2 flex-wrap">
            <span>First Scan · {fmtHebDate()}</span>
            <LivePulseIndicator />
          </div>
          <h1 className="text-2xl sm:text-3xl font-medium tracking-tight text-foreground">
            {greet(user.name)}
          </h1>
          <p className="text-xs text-zinc-400 mt-1 max-w-2xl leading-relaxed">
            Optygo Pulse ניתח את <span className="text-primary font-medium">נתוני ה-Priority שלך</span> וזיהה את הדובדבנים — סיכונים והזדמנויות במונחים של ₪.
          </p>
        </div>
        <div className="flex flex-col gap-2.5 lg:items-end">
          <div className="flex items-center gap-2 flex-wrap lg:justify-end">
            <button
              type="button"
              onClick={() => {
                track("exec_actions_open", "/home", {});
                setExecActionsOpen(true);
              }}
              className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-primary/40 bg-primary/15 hover:bg-primary/25 hover:border-primary/60 text-primary text-xs font-medium transition-all hover:shadow-[0_0_25px_-6px] hover:shadow-primary/60 animate-pulse-soft"
              data-testid="open-exec-actions"
              title='אם הייתי מנהל את העסק שלך — מה הייתי עושה השבוע?'
            >
              <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
              <span>3 הפעולות שלי השבוע</span>
            </button>
            <button
              type="button"
              onClick={() => {
                track("action_plan_open", "/home", {});
                setPlanOpen(true);
              }}
              className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border bg-card hover:bg-card/80 hover:border-primary/30 text-zinc-400 hover:text-primary text-xs font-medium transition-all"
              data-testid="open-action-plan"
              title="צור תוכנית פעולה חכמה ל-30 הימים הבאים"
            >
              <Target className="w-3.5 h-3.5" strokeWidth={2} />
              <span>תוכנית 30 יום</span>
            </button>
          </div>
          {headline && (
            <div className="lg:shrink-0 -mx-4 sm:-mx-5 lg:mx-0 px-4 sm:px-5 lg:px-0 overflow-x-auto lg:overflow-visible no-scrollbar">
              <TldrStrip data={headline} onNavigate={navigate} loading={loading} />
            </div>
          )}
        </div>
      </motion.div>

      {/* ===== Executive Actions Inline — THE wow moment, no clicks needed ===== */}
      <ExecutiveActionsInline />

      {/* ===== Dead Capital strip — confront frozen capital right on home ===== */}
      <DeadCapitalStrip />

      {/* ===== Cherry Picker — supporting evidence behind the actions ===== */}
      <CherryPicker
        onAskAi={(prompt) =>
          window.dispatchEvent(new CustomEvent("ai-copilot-ask", { detail: { prompt } }))
        }
      />

      {/* ===== Quick Jump (Command Center) ===== */}
      <QuickJumpBar
        invKpis={invKpis}
        summary={summary}
        zeroPrice={zeroPrice}
        onTheWay={onTheWay}
        families={families}
      />

      {/* ===== KPI Row ===== */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
        <KpiCard
          label="מחזור החודש"
          value={headline ? `₪${formatShort(currentMonthRevenue(monthly))}` : null}
          countUpNumeric={headline ? currentMonthRevenue(monthly) : null}
          countUpFormat={(n) => `₪${formatShort(n)}`}
          delta={headline?.trend_pct}
          deltaLabel={headline ? `${headline.current_month_name} מול ${headline.previous_month_name}` : null}
          accent="text-foreground"
          icon={TrendingUp}
          loading={loading}
          testId="kpi-revenue"
          explainKpiId="revenue"
        />
        <KpiCard
          label="עסקאות"
          value={summary ? summary.orders_count.toLocaleString() : null}
          countUpNumeric={summary?.orders_count}
          delta={null}
          deltaLabel="סך החשבוניות בתקופה"
          accent="text-foreground"
          icon={Receipt}
          loading={loading}
          testId="kpi-invoices"
          explainKpiId="transactions"
        />
        <KpiCard
          label="מגמת חודש"
          value={headline ? `${headline.trend_pct > 0 ? "+" : ""}${headline.trend_pct}%` : null}
          countUpNumeric={headline?.trend_pct}
          countUpFormat={(n) => `${n > 0 ? "+" : ""}${n.toFixed(1)}%`}
          delta={null}
          deltaLabel={
            headline
              ? headline.trend_pct < 0 && isPartialMonth()
                ? `${headline.current_month_name} עוד לא הסתיים`
                : `שינוי מול ${headline.previous_month_name}`
              : null
          }
          accent="text-foreground"
          tinyDelta={headline?.trend_pct}
          icon={headline && headline.trend_pct >= 0 ? TrendingUp : TrendingDown}
          loading={loading}
          testId="kpi-momentum"
          explainKpiId="momentum"
        />
            <KpiCard
          label="הון במלאי"
          value={invKpis?.available ? `₪${formatShort(invKpis.tied_up_capital)}` : "—"}
          countUpNumeric={invKpis?.available ? invKpis.tied_up_capital : null}
          countUpFormat={(n) => `₪${formatShort(n)}`}
          delta={null}
          deltaLabel={
            invKpis?.available
              ? `${invKpis.critical_restock_count} דחוף · ${invKpis.stockouts_count} חסרים · ${invKpis.dead_stock_count} ללא תנועה`
              : "טוען..."
          }
          accent="text-primary"
          icon={Boxes}
          loading={loading}
          testId="kpi-inventory"
          onClick={() => navigate("/inventory")}
        />
      </div>

      {/* ===== Chart + Insights ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        {/* Trend chart - 2/3 */}
        <div className="lg:col-span-2 bg-card border border-border rounded-lg p-4" data-testid="trend-card">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                מגמת מחזור
              </div>
              <h2 className="text-base font-medium mt-0.5">60 הימים האחרונים</h2>
            </div>
            <GranularityToggle value={granularity} onChange={setGranularity} />
          </div>
          {loading || !timeseries ? (
            <SkeletonChart />
          ) : (
            <TrendChart series={timeseries.series} branches={timeseries.branches} granularity={granularity} />
          )}
        </div>

        {/* Insight of the Day - 1/3 */}
        <div className="bg-card border border-border rounded-lg p-4 flex flex-col" data-testid="insights-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono flex items-center gap-1.5">
                <Lightbulb className="w-3 h-3 text-primary" />
                AI · תובנת היום
              </div>
              <h2 className="text-base font-medium mt-0.5">מה הכי חשוב כרגע</h2>
            </div>
            <button
              onClick={() => navigate("/insights")}
              className="text-[11px] text-zinc-500 hover:text-primary transition-colors flex items-center gap-1"
              data-testid="insight-of-day-see-all"
            >
              ראה הכל <ChevronLeft className="w-3 h-3" />
            </button>
          </div>
          {loading ? (
            <div className="flex-1 space-y-2"><SkeletonRow /><SkeletonRow /></div>
          ) : insights.length > 0 ? (
            <InsightOfTheDay
              insight={insights[0]}
              headline={headline}
              recommendations={recommendations}
              monthly={monthly}
              onNavigate={navigate}
            />
          ) : (
            <EmptyState icon={Lightbulb} title="אין תובנות זמינות" body="נסה לטעון מחדש או בדוק את החיבור ל-ERP." />
          )}
        </div>
      </div>

      {/* ===== Action Center + AI Recommendations ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 pb-4">
        {/* Action Center latest */}
        <div className="bg-card border border-border rounded-lg p-4" data-testid="action-center-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                Activity
              </div>
              <h2 className="text-base font-medium mt-0.5">מרכז פעולות</h2>
            </div>
            <button
              onClick={() => navigate("/actions")}
              className="text-[11px] text-zinc-500 hover:text-primary transition-colors flex items-center gap-1"
            >
              ראה הכל <ChevronLeft className="w-3 h-3" />
            </button>
          </div>
          {loading ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => <SkeletonRow key={i} />)}
            </div>
          ) : marked.length === 0 ? (
            <SocialProofEmpty recommendations={recommendations} onNavigate={navigate} />
          ) : (
            <div className="space-y-2">
              {marked.slice(0, 4).map((m, i) => <ActionRow key={`${m.id}-${i}`} action={m} index={i} />)}
            </div>
          )}
        </div>

        {/* AI Recommendations */}
        <div className="bg-card border border-border rounded-lg p-4" data-testid="recommendations-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono flex items-center gap-1.5">
                  <Lightbulb className="w-3 h-3 text-primary" />
                  AI Recommendations
                </div>
                <h2 className="text-base font-medium mt-0.5">המלצות פעולה</h2>
              </div>
            </div>
            <button
              onClick={() => navigate("/insights")}
              className="text-[11px] text-zinc-500 hover:text-primary transition-colors flex items-center gap-1"
            >
              ראה הכל <ChevronLeft className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-2">
            {loading
              ? [0, 1, 2, 3].map((i) => <SkeletonRow key={i} />)
              : recommendations.slice(0, 4).map((r, i) => (
                  <RecommendationRow key={`${r.sku}-${i}`} rec={r} index={i} />
                ))}
          </div>
        </div>
      </div>
      {/* ===== Action Plan Modal ===== */}
      <ActionPlanModal open={planOpen} onClose={() => setPlanOpen(false)} />
      {/* ===== Executive Actions Modal — "What would you do this week?" ===== */}
      <ExecutiveActionsModal open={execActionsOpen} onClose={() => setExecActionsOpen(false)} />
    </div>
  );
}

/* ───────────────────────── Helpers ───────────────────────── */

function formatShort(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  return Math.round(n).toLocaleString();
}

function currentMonthRevenue(monthly) {
  if (!monthly || !monthly.length) return null;
  return monthly[monthly.length - 1].total;
}

function extractMonthlyTotals(monthly) {
  return (monthly || []).map((m) => m.total);
}

/* ───────────────────────── KPI Card ───────────────────────── */

function KpiCard({ label, value, delta, deltaLabel, accent = "text-foreground", icon: Icon, sparkData, loading, testId, onClick, tinyDelta, countUpNumeric, countUpPrefix, countUpSuffix, countUpFormat, explainKpiId }) {
  const positive = (delta ?? 0) >= 0;
  const tinyPositive = (tinyDelta ?? 0) >= 0;
  const Wrap = onClick ? "button" : "div";
  return (
    <Wrap
      onClick={onClick}
      className={`group relative bg-card border border-border rounded-lg p-4 overflow-hidden transition-all duration-300 hover:border-primary/30 hover:bg-card/80 text-right ${
        onClick ? "cursor-pointer hover:-translate-y-0.5" : ""
      }`}
      data-testid={testId}
    >
      <div className="absolute inset-0 bg-gradient-to-bl from-primary/[0.04] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />

      <div className="flex items-center justify-between mb-2 relative">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono flex items-center gap-1.5">
          <span>{label}</span>
          {explainKpiId && !loading && (
            <ExplainButton kpi={explainKpiId} />
          )}
        </div>
        {Icon && (
          <div className="w-6 h-6 rounded-md bg-[#0A0A0E] border border-border flex items-center justify-center">
            <Icon className={`w-3 h-3 ${accent}`} strokeWidth={1.5} />
          </div>
        )}
      </div>

      {loading || value == null ? (
        <SkeletonText className="h-7 w-20" />
      ) : (
        <div className={`text-2xl font-light tracking-tight ${accent} relative inline-flex items-baseline gap-1.5`}>
          {countUpNumeric != null ? (
            <CountUp
              value={countUpNumeric}
              prefix={countUpPrefix || ""}
              suffix={countUpSuffix || ""}
              format={countUpFormat}
            />
          ) : (
            <span>{value}</span>
          )}
          {tinyDelta != null && (
            <span
              className={`text-[10px] font-mono ${tinyPositive ? "text-severity-opportunity/70" : "text-zinc-500"}`}
            >
              {tinyPositive ? "▲" : "▼"}
            </span>
          )}
        </div>
      )}

      <div className="flex items-center justify-between mt-2 relative">
        <div className="text-[10px] text-zinc-500 leading-tight">{deltaLabel}</div>
        {delta != null && (
          <div
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[10px] font-semibold ${
              positive
                ? "bg-severity-opportunity/10 text-severity-opportunity"
                : "bg-severity-critical/10 text-severity-critical"
            }`}
          >
            {positive ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
            {Math.abs(delta)}%
          </div>
        )}
      </div>

      {sparkData && sparkData.length > 1 && <Sparkline data={sparkData} color={accent} />}
    </Wrap>
  );
}

// True if today is not the last day of its month — used to soften "negative momentum" framing
function isPartialMonth() {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  return tomorrow.getMonth() === today.getMonth();
}

function Sparkline({ data, color }) {
  const w = 100;
  const h = 28;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const step = w / (data.length - 1);
  const path = data
    .map((v, i) => `${i === 0 ? "M" : "L"}${i * step},${h - ((v - min) / range) * h}`)
    .join(" ");
  // Render as a wide, subtle backdrop at the very bottom of the card so it never
  // overlaps the delta pill / label row above it.
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="absolute inset-x-0 bottom-0 w-full h-7 opacity-[0.12] group-hover:opacity-30 transition-opacity pointer-events-none"
      style={{ direction: "ltr" }}
    >
      <path d={path} fill="none" stroke="currentColor" strokeWidth="1.25" className={color} />
    </svg>
  );
}

/* ───────────────────────── TL;DR Strip ───────────────────────── */

function TldrStrip({ data, onNavigate, loading }) {
  if (loading) return <div className="h-9 w-72 bg-card rounded-full animate-pulse" />;
  const trendDown = (data.trend_pct ?? 0) < 0;
  const TrendIcon = trendDown ? TrendingDown : TrendingUp;
  const trendColor = trendDown ? "text-severity-critical" : "text-severity-opportunity";

  return (
    <div
      className="inline-flex items-stretch bg-card border border-border rounded-md text-xs overflow-hidden whitespace-nowrap"
      data-testid="tldr-strip"
    >
      <button
        onClick={() => onNavigate("/actions?tab=growers")}
        className="px-3 py-1.5 hover:bg-primary/8 transition-colors flex items-center gap-1.5 border-l border-border"
      >
        <Sparkles className="w-3 h-3 text-primary" />
        <span className="font-mono font-semibold text-foreground">{data.opportunities}</span>
        <span className="text-zinc-400">צמיחה</span>
      </button>
      <button
        onClick={() => onNavigate("/actions?tab=price_variance")}
        className="px-3 py-1.5 hover:bg-severity-watch/8 transition-colors flex items-center gap-1.5 border-l border-border"
      >
        <AlertTriangle className="w-3 h-3 text-severity-watch" />
        <span className="font-mono font-semibold text-foreground">{data.risks}</span>
        <span className="text-zinc-400">פערים</span>
      </button>
      <button
        onClick={() => onNavigate("/actions?tab=cost_anomalies")}
        className="px-3 py-1.5 hover:bg-card/60 transition-colors flex items-center gap-1.5 border-l border-border"
      >
        <Search className="w-3 h-3 text-severity-watch" />
        <span className="font-mono font-semibold text-foreground">{data.anomalies}</span>
        <span className="text-zinc-400">חשד</span>
      </button>
      <div className="px-3 py-1.5 flex items-center gap-1.5">
        <TrendIcon className={`w-3 h-3 ${trendColor}`} />
        <span className={`font-mono font-semibold ${trendColor}`}>
          {data.trend_pct > 0 ? "+" : ""}{data.trend_pct}%
        </span>
        <span className="text-zinc-500 text-[10px]">
          {data.current_month_name} / {data.previous_month_name}
        </span>
      </div>
    </div>
  );
}

/* ───────────────────────── Granularity Toggle ───────────────────────── */

function GranularityToggle({ value, onChange }) {
  const opts = [
    { id: "day", label: "יום" },
    { id: "week", label: "שבוע" },
    { id: "month", label: "חודש" },
  ];
  return (
    <div className="inline-flex bg-[#0A0A0E] border border-border rounded-lg p-0.5">
      {opts.map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          className={`text-[11px] px-3 py-1 rounded-md font-medium transition-all ${
            value === o.id ? "bg-primary text-primary-foreground" : "text-zinc-400 hover:text-foreground"
          }`}
          data-testid={`granularity-${o.id}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ───────────────────────── Trend Chart ───────────────────────── */

function TrendChart({ series, branches, granularity }) {
  // Normalize: backend uses `date`, the chart works with `day` everywhere.
  const normalised = useMemo(
    () => (series || []).map((r) => ({ ...r, day: r.day || r.date })),
    [series]
  );

  // Aggregate by granularity
  const data = useMemo(() => {
    if (granularity === "day") return normalised;
    const bucket = (d) => {
      const dt = new Date(d);
      if (granularity === "week") {
        const day = dt.getDay();
        const monday = new Date(dt);
        monday.setDate(dt.getDate() - day);
        return monday.toISOString().slice(0, 10);
      }
      return d.slice(0, 7);
    };
    const map = {};
    for (const row of normalised) {
      const k = bucket(row.day);
      if (!map[k]) map[k] = { day: k, total: 0 };
      map[k].total += row.total || 0;
      for (const b of branches) {
        map[k][b] = (map[k][b] || 0) + (row[b] || 0);
      }
    }
    return Object.values(map);
  }, [normalised, granularity, branches]);

  const fmtTick = (d) => {
    if (granularity === "month") return d.slice(5, 7) + "/" + d.slice(2, 4);
    const date = new Date(d);
    return `${date.getDate()}/${date.getMonth() + 1}`;
  };

  return (
    <div dir="ltr" className="w-full h-48" data-testid="trend-chart">
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 10, right: 0, left: -10, bottom: 0 }}>
          <defs>
            <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.25} />
              <stop offset="100%" stopColor="#00E5FF" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="day"
            tickFormatter={fmtTick}
            tick={{ fill: "#71717A", fontSize: 10, fontFamily: "monospace" }}
            axisLine={{ stroke: "#27272A" }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(v) => `₪${(v / 1000).toFixed(0)}K`}
            tick={{ fill: "#71717A", fontSize: 10, fontFamily: "monospace" }}
            axisLine={false}
            tickLine={false}
            width={60}
          />
          <Tooltip
            contentStyle={{
              background: "rgba(11,11,14,0.95)",
              border: "1px solid #27272A",
              borderRadius: 8,
              direction: "rtl",
              fontSize: 12,
            }}
            labelFormatter={fmtTick}
            formatter={(v) => `₪${formatShort(v)}`}
          />
          <Area type="monotone" dataKey="total" stroke="#00E5FF" strokeWidth={2} fill="url(#trendGrad)" animationDuration={1200} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ───────────────────────── Insight Row ───────────────────────── */

const SEVERITY_STYLES = {
  opportunity: { dot: "bg-severity-opportunity", text: "text-severity-opportunity" },
  watch: { dot: "bg-severity-watch", text: "text-severity-watch" },
  critical: { dot: "bg-severity-critical", text: "text-severity-critical" },
  anomaly: { dot: "bg-severity-anomaly", text: "text-severity-anomaly" },
};

const INSIGHT_TAB_MAP = {
  growers: "growers",
  "price-variance": "price_variance",
  "cost-anomaly": "cost_anomalies",
};

function InsightRow({ insight, index, onNavigate }) {
  const sty = SEVERITY_STYLES[insight.severity] || SEVERITY_STYLES.watch;
  const tab = INSIGHT_TAB_MAP[insight.type];
  return (
    <motion.button
      initial={{ opacity: 0, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      onClick={() => {
        track("insight_card_click", "/", { type: insight.type });
        onNavigate(tab ? `/actions?tab=${tab}` : "/actions");
      }}
      className="group w-full text-right flex items-start gap-3 p-3 rounded-md hover:bg-[#0A0A0E] transition-colors"
      data-testid={`insight-row-${index}`}
    >
      <div className={`w-1.5 h-1.5 rounded-full ${sty.dot} shrink-0 mt-2`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground truncate">{insight.title}</div>
        <div className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed line-clamp-2">
          {insight.description}
        </div>
      </div>
    </motion.button>
  );
}

/* ───────────────────────── Insight of the Day (hero) ───────────────────────── */

function InsightOfTheDay({ insight, headline, recommendations, monthly, onNavigate }) {
  const sty = SEVERITY_STYLES[insight.severity] || SEVERITY_STYLES.watch;
  const tab = INSIGHT_TAB_MAP[insight.type];

  // Build supporting stats based on insight type
  const stats = useMemo(() => {
    if (!headline) return [];
    if (insight.type === "growers") {
      return [
        { label: "פריטים", value: headline.opportunities },
        { label: "חלון בדיקה", value: "30 ימים" },
        { label: "סף גידול", value: "40%+" },
      ];
    }
    if (insight.type === "price-variance") {
      return [
        { label: "פריטים", value: headline.risks },
        { label: "מינ' חשבוניות", value: "10" },
        { label: "פער ממוצע", value: ">15%" },
      ];
    }
    if (insight.type === "cost-anomaly") {
      return [
        { label: "פריטים", value: headline.anomalies },
        { label: "יחס מחיר/עלות", value: "<70%" },
        { label: "מינ' שורות", value: "3" },
      ];
    }
    return [];
  }, [insight.type, headline]);

  // Pick a lead example from recommendations (matching type when possible)
  const example = useMemo(() => {
    if (!recommendations?.length) return null;
    return recommendations[0];
  }, [recommendations]);

  // Mini sparkline data
  const sparkData = useMemo(() => extractMonthlyTotals(monthly).slice(-8), [monthly]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="group flex-1 text-right w-full rounded-md border border-border bg-[#0A0A0E]/40 p-4 transition-all flex flex-col gap-3"
      data-testid="insight-of-the-day"
    >
      {/* Top: severity badge + sparkline */}
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className={`w-1.5 h-1.5 rounded-full ${sty.dot}`} />
            <span className={`text-[10px] uppercase tracking-widest font-mono ${sty.text}`}>
              {insight.severity === "opportunity" ? "הזדמנות"
                : insight.severity === "critical" ? "סיכון"
                : insight.severity === "anomaly" ? "חשד" : "לתשומת לב"}
            </span>
          </div>
          <div className="text-sm font-semibold text-foreground leading-snug">{insight.title}</div>
        </div>
        {sparkData.length > 1 && (
          <MiniSparkline data={sparkData} color={sty.text} />
        )}
      </div>

      {/* Stats strip */}
      {stats.length > 0 && (
        <div className="grid grid-cols-3 gap-2 py-2 border-y border-border/40">
          {stats.map((s, i) => (
            <div key={i} className="text-center">
              <div className={`text-base font-mono font-semibold ${sty.text} leading-none`}>{s.value}</div>
              <div className="text-[9px] text-zinc-500 uppercase tracking-wider mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Lead example */}
      {example && insight.type === "growers" && (
        <div className="flex items-center gap-2 text-[11px] bg-[#0A0A0E]/60 rounded px-2.5 py-2 border border-border/40">
          <span className="text-zinc-500 shrink-0">דוגמא:</span>
          <span className="text-foreground truncate flex-1">{example.product}</span>
          <span className="text-primary font-mono shrink-0">{example.recommended_purchase_qty?.toLocaleString()} יח׳</span>
        </div>
      )}

      {/* Description (compact) */}
      <div className="text-[11px] text-zinc-400 leading-relaxed line-clamp-2 flex-1">
        {insight.description}
      </div>

      {/* Dual CTA */}
      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border/40">
        <button
          onClick={(e) => {
            e.stopPropagation();
            track("insight_of_day_see_all", "/", { type: insight.type });
            onNavigate(tab ? `/actions?tab=${tab}` : "/actions");
          }}
          className="text-[11px] py-1.5 rounded-md bg-primary/10 text-primary hover:bg-primary/15 transition-colors flex items-center justify-center gap-1.5"
          data-testid="insight-of-day-cta-primary"
        >
          ראה את כולם <ArrowLeft className="w-3 h-3" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            track("insight_of_day_ask_ai", "/", { type: insight.type });
            window.dispatchEvent(new CustomEvent("ai-copilot-ask", { detail: { prompt: insight.title } }));
          }}
          className="text-[11px] py-1.5 rounded-md border border-border text-zinc-400 hover:text-foreground hover:border-primary/40 transition-colors flex items-center justify-center gap-1.5"
          data-testid="insight-of-day-cta-secondary"
        >
          <Sparkles className="w-3 h-3" /> שאל את ה-AI
        </button>
      </div>
    </motion.div>
  );
}

function MiniSparkline({ data, color }) {
  const w = 56, h = 24;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const step = w / (data.length - 1);
  const path = data
    .map((v, i) => `${i === 0 ? "M" : "L"}${i * step},${h - ((v - min) / range) * h}`)
    .join(" ");
  return (
    <svg width={w} height={h} style={{ direction: "ltr" }} className="shrink-0">
      <path d={path} fill="none" stroke="currentColor" strokeWidth="1.5" className={color} opacity="0.7" />
    </svg>
  );
}

/* ───────────────────────── Action Row (Marked) ───────────────────────── */

function ActionRow({ action, index }) {
  const date = new Date(action.executed_at).toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit" });
  const title = action.payload?.product || action.payload?.invoice || action.insight_id;
  return (
    <motion.div
      initial={{ opacity: 0, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      className="flex items-start gap-3 p-3 rounded-lg bg-[#0A0A0E]/40 border border-border/50"
      data-testid={`action-row-${index}`}
    >
      <div className="w-1.5 h-1.5 rounded-full bg-primary shrink-0 mt-2" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground truncate">{title}</div>
        <div className="text-[11px] text-zinc-500 mt-0.5 font-mono">
          {action.payload?.type || "סומן לבדיקה"}
        </div>
      </div>
      <div className="text-[10px] text-zinc-600 font-mono shrink-0 pt-1">{date}</div>
    </motion.div>
  );
}

/* ───────────────────────── Recommendation Row ───────────────────────── */

const STRENGTH_META = {
  high: { dot: "bg-severity-opportunity", label: "מומלץ בהחלט" },
  medium: { dot: "bg-primary", label: "מומלץ" },
  soft: { dot: "bg-zinc-500", label: "לשקול" },
};

function RecommendationRow({ rec, index }) {
  const meta = STRENGTH_META[rec.action_strength] || STRENGTH_META.soft;
  return (
    <motion.div
      initial={{ opacity: 0, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      className="flex items-start gap-3 p-3 rounded-md hover:bg-[#0A0A0E] transition-colors"
      data-testid={`recommendation-row-${index}`}
    >
      <div className="w-8 h-8 rounded-md bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/20 flex items-center justify-center shrink-0">
        <ShoppingCart className="w-3.5 h-3.5 text-primary" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-foreground truncate leading-snug">{rec.product}</div>
        <div className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed line-clamp-1">{rec.action}</div>
        <div className="flex items-center gap-2 mt-1">
          <span className={`text-[9px] uppercase tracking-widest font-mono ${meta.dot.replace("bg-", "text-")}`}>
            {meta.label}
          </span>
          {!rec.baseline_thin && rec.recommended_purchase_qty != null && (
            <span className="text-[10px] text-zinc-600 font-mono">
              · {rec.recommended_purchase_qty.toLocaleString()} יח׳
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/* ───────────────────────── Social Proof (empty state replacement) ───────────────────────── */

function SocialProofEmpty({ recommendations, onNavigate }) {
  const items = (recommendations || []).slice(0, 3);
  if (items.length === 0) {
    return (
      <EmptyState
        icon={Target}
        title="עדיין לא סימנת כלום"
        body="לחץ 'סמן לבדיקה' על פריט במרכז הפעולות והוא יופיע כאן."
      />
    );
  }
  return (
    <div className="space-y-2" data-testid="social-proof-empty">
      <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-primary/[0.05] border border-primary/15 mb-1">
        <Sparkles className="w-3 h-3 text-primary shrink-0" />
        <span className="text-[11px] text-zinc-400 leading-relaxed">
          <span className="text-foreground font-medium">המומלצים השבוע</span> · פריטים שעלו אצל משתמשים אחרים
        </span>
      </div>
      {items.map((rec, i) => (
        <button
          key={`${rec.sku}-${i}`}
          onClick={() => {
            track("social_proof_click", "/", { sku: rec.sku });
            onNavigate("/insights");
          }}
          className="w-full text-right flex items-center gap-3 p-2 rounded-md hover:bg-[#0A0A0E] transition-colors"
          data-testid={`social-proof-row-${i}`}
        >
          <div className="w-6 h-6 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
            <span className="text-[10px] font-mono text-primary">{i + 1}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium truncate">{rec.product}</div>
            <div className="text-[10px] text-zinc-500 truncate">{rec.action}</div>
          </div>
        </button>
      ))}
    </div>
  );
}

/* ───────────────────────── Skeletons + Empty State ───────────────────────── */

function SkeletonText({ className = "" }) {
  return (
    <div className={`relative overflow-hidden bg-[#141416] rounded-md ${className}`}>
      <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.6s_infinite] bg-gradient-to-r from-transparent via-white/[0.04] to-transparent" />
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="flex items-start gap-3 p-3">
      <div className="w-1.5 h-1.5 rounded-full bg-zinc-800 mt-2" />
      <div className="flex-1 space-y-1.5">
        <SkeletonText className="h-3 w-3/4" />
        <SkeletonText className="h-2.5 w-1/2" />
      </div>
    </div>
  );
}

function SkeletonChart() {
  return (
    <div className="relative overflow-hidden bg-[#0A0A0E] rounded-md h-48">
      <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.6s_infinite] bg-gradient-to-r from-transparent via-white/[0.03] to-transparent" />
    </div>
  );
}

function EmptyState({ icon: Icon, title, body }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <Icon className="w-7 h-7 text-zinc-700 mb-3" strokeWidth={1.25} />
      <div className="text-sm text-zinc-400">{title}</div>
      <div className="text-[11px] text-zinc-600 mt-1 max-w-[200px] leading-relaxed">{body}</div>
    </div>
  );
}
